//Order.js - Modern Order Confirmation Page
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import styled, { keyframes } from "styled-components";
import QRCode from 'qrcode.react';
import api from '../../Api';
import { LoadingOverlay, LoadingSpinner } from "../../globalStyles.js";

// Status configurations
const statusConfig = {
  0: {
    label: 'Payment Pending',
    color: '#f59e0b',
    bgColor: '#fef3c7',
    icon: '⏳',
    message: 'Your payment proof is being reviewed. We will confirm your booking shortly.'
  },
  1: {
    label: 'Confirmed',
    color: '#10b981',
    bgColor: '#d1fae5',
    icon: '✓',
    message: 'Your booking is confirmed! Show the QR code below for attendance.'
  },
  9: {
    label: 'Cancelled',
    color: '#ef4444',
    bgColor: '#fee2e2',
    icon: '✕',
    message: 'This booking has been cancelled. Please contact support for assistance.'
  }
};

export const Order = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useParams();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/classBooking/api/orders/${token}`);
        const orderData = res.data;
        orderData.token = token;
        setOrder(orderData);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <LoadingOverlay>
        <LoadingSpinner />
      </LoadingOverlay>
    );
  }

  if (!order) {
    return (
      <PageContainer>
        <ErrorCard>
          <ErrorIcon>🔍</ErrorIcon>
          <ErrorTitle>Order Not Found</ErrorTitle>
          <ErrorText>We couldn't find this order. Please check the link and try again.</ErrorText>
        </ErrorCard>
      </PageContainer>
    );
  }

  const orderDetails = order.order_details;
  const participantDetails = order.participant_info;
  const status = statusConfig[order.status] || statusConfig[0];
  const isConfirmed = order.status === 1;

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader>
        <BackButton onClick={() => navigate('/en/bookings')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </BackButton>
        <PageTitle>Order Details</PageTitle>
        <div style={{ width: 40 }}></div>
      </PageHeader>

      {/* Status Banner */}
      <StatusBanner $color={status.color} $bgColor={status.bgColor}>
        <StatusIconWrapper $color={status.color}>
          {order.status === 1 ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : order.status === 0 ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          ) : (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </StatusIconWrapper>
        <StatusContent>
          <StatusLabel $color={status.color}>{status.label}</StatusLabel>
          <StatusMessage>{status.message}</StatusMessage>
        </StatusContent>
      </StatusBanner>

      {/* QR Code Section - Only show when confirmed */}
      {isConfirmed && (
        <QRSection>
          <QRCard>
            <QRTitle>Your Ticket</QRTitle>
            <QRSubtitle>Scan for attendance</QRSubtitle>
            <QRCodeWrapper>
              <QRCode 
                value={`https://booking.dynevents.com/admin/checkin/?token=${token}`} 
                size={180}
                level="H"
              />
            </QRCodeWrapper>
            <QRNote>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>Please screenshot this page for reference</span>
            </QRNote>
          </QRCard>
        </QRSection>
      )}

      {/* Order ID */}
      <OrderIdCard>
        <OrderIdLabel>Order ID</OrderIdLabel>
        <OrderIdValue>#{order.id}</OrderIdValue>
      </OrderIdCard>

      {/* Program Details Section */}
      <ModernSection>
        <SectionHeader $color="blue">
          <SectionIconWrapper>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SectionIconWrapper>
          <SectionTitle>Program Details</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <ProgramName>{orderDetails?.programName}</ProgramName>
          <DetailGrid>
            {orderDetails?.option1value && (
              <DetailItem>
                <DetailLabel>Option</DetailLabel>
                <DetailValue>{orderDetails?.option1value}</DetailValue>
              </DetailItem>
            )}
            {orderDetails?.option2value && (
              <DetailItem>
                <DetailLabel>Selection</DetailLabel>
                <DetailValue>{orderDetails?.option2value}</DetailValue>
              </DetailItem>
            )}
            {orderDetails?.venue && (
              <DetailItem>
                <DetailLabel>Venue</DetailLabel>
                <DetailValue>{orderDetails?.venue}</DetailValue>
              </DetailItem>
            )}
            <DetailItem>
              <DetailLabel>Price</DetailLabel>
              <DetailValue $highlight>
                {orderDetails?.fee === "0" ? 'Free' : `HK$ ${orderDetails?.totalPrice || orderDetails?.fee}`}
              </DetailValue>
            </DetailItem>
          </DetailGrid>

          {/* Selected Class Dates */}
          {orderDetails?.selectedDates && orderDetails.selectedDates.length > 0 && (
            <ClassDatesSection>
              <ClassDatesTitle>
                📅 Your {orderDetails.selectedDates.length} Scheduled Classes
              </ClassDatesTitle>
              <ClassTimeline>
                {/* Sort dates: valid dates first (chronologically), then TBD dates */}
                {[...orderDetails.selectedDates]
                  .sort((a, b) => {
                    const dateA = a.date ? new Date(a.date) : null;
                    const dateB = b.date ? new Date(b.date) : null;
                    
                    // If both have dates, sort chronologically
                    if (dateA && dateB) return dateA - dateB;
                    // If only a has date, a comes first
                    if (dateA && !dateB) return -1;
                    // If only b has date, b comes first
                    if (!dateA && dateB) return 1;
                    // If neither has date, keep original order
                    return 0;
                  })
                  .map((classDate, index, arr) => {
                    const hasValidDate = classDate.date && !isNaN(new Date(classDate.date).getTime());
                    const dateObj = hasValidDate ? new Date(classDate.date) : null;
                    
                    return (
                      <TimelineItem key={classDate.id || index} $isLast={index === arr.length - 1}>
                        <TimelineDot $isTBD={!hasValidDate} />
                        <TimelineCard $isTBD={!hasValidDate}>
                          {hasValidDate ? (
                            <TimelineDateBadge>
                              <span className="day">{dateObj.getDate()}</span>
                              <span className="month">{dateObj.toLocaleDateString('en-GB', { month: 'short' })}</span>
                            </TimelineDateBadge>
                          ) : (
                            <TimelineDateBadgeTBD>
                              <span className="tbd">TBD</span>
                            </TimelineDateBadgeTBD>
                          )}
                          <TimelineContent>
                            <TimelineClassName>{classDate.name || `Class ${index + 1}`}</TimelineClassName>
                            <TimelineWeekday $isTBD={!hasValidDate}>
                              {hasValidDate 
                                ? dateObj.toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric' })
                                : 'Date to be announced'
                              }
                            </TimelineWeekday>
                            {hasValidDate && (
                              <TimelineTime>
                                🕐 {dateObj.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true })}
                              </TimelineTime>
                            )}
                          </TimelineContent>
                        </TimelineCard>
                      </TimelineItem>
                    );
                  })}
              </ClassTimeline>
            </ClassDatesSection>
          )}
        </SectionContent>
      </ModernSection>

      {/* Participant Info Section */}
      <ModernSection>
        <SectionHeader $color="purple">
          <SectionIconWrapper>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SectionIconWrapper>
          <SectionTitle>Participant Info</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Name</DetailLabel>
              <DetailValue>{participantDetails?.firstname} {participantDetails?.lastname}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Date of Birth</DetailLabel>
              <DetailValue>
                {participantDetails?.dob 
                  ? new Date(participantDetails.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : '-'
                }
              </DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Gender</DetailLabel>
              <DetailValue>{participantDetails?.gender}</DetailValue>
            </DetailItem>
          </DetailGrid>
        </SectionContent>
      </ModernSection>

      {/* Contact Info Section */}
      <ModernSection>
        <SectionHeader $color="teal">
          <SectionIconWrapper>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7294C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1469 21.5901 20.9046 21.7335 20.6408 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.271 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00027 4.10999 2H7.10999C7.5953 1.99522 8.06579 2.16708 8.43376 2.48353C8.80173 2.79999 9.04207 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.8939 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5765 14.4765 14.08 15.9L15.35 14.63C15.6219 14.3611 15.9651 14.1758 16.3391 14.0961C16.7131 14.0163 17.1021 14.0455 17.46 14.18C18.3673 14.5185 19.3099 14.7534 20.27 14.88C20.7558 14.9485 21.1996 15.1932 21.5177 15.5673C21.8359 15.9414 22.0057 16.4187 22 16.92Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SectionIconWrapper>
          <SectionTitle>Contact Info</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <DetailGrid>
            <DetailItem>
              <DetailLabel>Mobile</DetailLabel>
              <DetailValue>{order.contact}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Email</DetailLabel>
              <DetailValue>{order.email}</DetailValue>
            </DetailItem>
          </DetailGrid>
        </SectionContent>
      </ModernSection>

      {/* Additional Info Section - Only show if additionalInfo exists */}
      {orderDetails?.additionalInfo && Object.keys(orderDetails.additionalInfo).length > 0 && (
        <ModernSection>
          <SectionHeader $color="orange">
            <SectionIconWrapper>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </SectionIconWrapper>
          <SectionTitle>Additional Info</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <DetailGrid>
            {Object.entries(orderDetails.additionalInfo).map(([key, value]) => (
              <AdditionalInfoRow key={key}>
                <AdditionalInfoLabel>
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </AdditionalInfoLabel>
                <AdditionalInfoValue>
                  {Array.isArray(value) 
                    ? value.map(item => item.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ')
                    : value
                  }
                </AdditionalInfoValue>
              </AdditionalInfoRow>
            ))}
          </DetailGrid>
        </SectionContent>
        </ModernSection>
      )}

      {/* Footer */}
      <FooterSpacer />
      <Footer>
        <FooterButton onClick={() => navigate('/en/bookings')}>
          <span>View All Bookings</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </FooterButton>
      </Footer>
    </PageContainer>
  );
};

export default Order;

// ============================================
// STYLED COMPONENTS
// ============================================

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%);
  padding-bottom: 100px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const BackButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background: #f3f4f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;

  &:hover {
    background: #e5e7eb;
    transform: scale(1.05);
  }
`;

const PageTitle = styled.h1`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
`;

// Status Banner
const StatusBanner = styled.div`
  margin: 16px;
  padding: 20px;
  background: ${props => props.$bgColor};
  border: 2px solid ${props => props.$color};
  border-radius: 16px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const StatusIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.$color};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusLabel = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.$color};
  margin: 0 0 6px 0;
`;

const StatusMessage = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

// QR Code Section
const QRSection = styled.div`
  margin: 16px;
`;

const QRCard = styled.div`
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  border-radius: 20px;
  padding: 28px;
  text-align: center;
`;

const QRTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin: 0 0 4px 0;
`;

const QRSubtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 0 20px 0;
`;

const QRCodeWrapper = styled.div`
  background: white;
  padding: 16px;
  border-radius: 16px;
  display: inline-block;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const QRNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: white;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
`;

// Order ID Card
const OrderIdCard = styled.div`
  margin: 16px;
  padding: 16px 20px;
  background: white;
  border-radius: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
`;

const OrderIdLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
`;

const OrderIdValue = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #1a73e8;
`;

// Modern Section
const ModernSection = styled.div`
  margin: 16px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const SectionHeader = styled.div`
  background: ${props => {
    switch(props.$color) {
      case 'blue': return 'linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%)';
      case 'purple': return 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)';
      case 'teal': return 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)';
      case 'orange': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      default: return 'linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%)';
    }
  }};
  padding: 18px 24px;
  display: flex;
  align-items: center;
  gap: 14px;
`;

const SectionIconWrapper = styled.div`
  width: 42px;
  height: 42px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const SectionContent = styled.div`
  padding: 24px;
`;

const ProgramName = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
`;

const DetailGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 12px;
`;

const DetailLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`;

const DetailValue = styled.span`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$highlight ? '#1a73e8' : '#1a1a1a'};
`;

// Class Dates Section - Timeline Style
const ClassDatesSection = styled.div`
  margin-top: 20px;
`;

const ClassDatesTitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
`;

const ClassTimeline = styled.div`
  position: relative;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 16px;
  position: relative;
  padding-bottom: ${props => props.$isLast ? '0' : '16px'};

  &::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 18px;
    bottom: 0;
    width: 2px;
    background: ${props => props.$isLast ? 'transparent' : 'linear-gradient(to bottom, #1a73e8, #e5e7eb)'};
  }
`;

const TimelineDot = styled.div`
  width: 16px;
  height: 16px;
  background: ${props => props.$isTBD ? '#f3f4f6' : 'white'};
  border: 3px solid ${props => props.$isTBD ? '#9ca3af' : '#1a73e8'};
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
  z-index: 1;
`;

const TimelineCard = styled.div`
  flex: 1;
  display: flex;
  gap: 14px;
  padding: 14px;
  background: ${props => props.$isTBD ? '#fafafa' : 'white'};
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid ${props => props.$isTBD ? '#e5e7eb' : '#f0f0f0'};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateX(4px);
  }
`;

const TimelineDateBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  border-radius: 12px;
  flex-shrink: 0;

  .day {
    font-family: 'Poppins', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: white;
    line-height: 1;
  }

  .month {
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    text-transform: uppercase;
    line-height: 1;
    margin-top: 2px;
  }
`;

const TimelineDateBadgeTBD = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
  border-radius: 12px;
  flex-shrink: 0;

  .tbd {
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: white;
    letter-spacing: 0.5px;
  }
`;

const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

const TimelineClassName = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 3px 0;
`;

const TimelineWeekday = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: ${props => props.$isTBD ? '#f59e0b' : '#9ca3af'};
  font-style: ${props => props.$isTBD ? 'italic' : 'normal'};
  margin: 0;
`;

const TimelineTime = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  color: #1a73e8;
  font-weight: 500;
  margin: 4px 0 0 0;
`;

// Error Card
const ErrorCard = styled.div`
  margin: 40px 20px;
  padding: 40px;
  background: white;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const ErrorTitle = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const ErrorText = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

// Footer
const FooterSpacer = styled.div`
  height: 80px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 20px;
  background: white;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 1000;
`;

const FooterButton = styled.button`
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1a73e8 0%, #0d5bba 100%);
  color: white;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(26, 115, 232, 0.35);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(4px);
  }
`;

// Additional Info - Vertical Layout
const AdditionalInfoRow = styled.div`
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 12px;
`;

const AdditionalInfoLabel = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  margin: 0 0 8px 0;
  text-transform: capitalize;
`;

const AdditionalInfoValue = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.5;
`;

